import { useState, useCallback, useEffect } from 'react';
import { ColumnConfig } from '@/shared/types/table';
import {
  toggleColumnVisibility,
  setColumnVisibility,
  toggleColumnLock,
  resizeColumn,
  reorderColumns,
} from '../utils/columnManagement';

/**
 * Evita `return prev` en el merge cuando solo cambian `render` y/o `accessor` (identidad),
 * p. ej. la columna de acciones que depende de estado externo (carrito, etc.).
 */
function areColumnCellDefinitionsEqual<T>(
  col: ColumnConfig<T>,
  prev: ColumnConfig<T>,
): boolean {
  const renderMatches =
    (col.render === undefined && prev.render === undefined) ||
    (col.render !== undefined &&
      prev.render !== undefined &&
      col.render === prev.render);
  if (!renderMatches) return false;
  return col.accessor === prev.accessor;
}

/**
 * Hook que mantiene el estado de las columnas (orden, visibilidad, bloqueo, ancho)
 * y lo sincroniza con las columnas que pasa el padre.
 *
 * Cuando el padre actualiza las columnas (p. ej. columnOptions cargadas por API
 * tras React Query), este efecto actualiza el estado interno para que los dropdowns
 * y el resto de celdas reciban las opciones correctas. Se preservan width, visible,
 * locked y el orden actual si el conjunto de columnas no cambia (solo el contenido).
 *
 * Si el padre pasa una **nueva referencia** de `render` o `accessor` (función) en una
 * columna, el estado interno se sincroniza para que las celdas (p. ej. acciones) no
 * queden con closures obsoletos.
 *
 * Recomendación: el padre debe memoizar las columnas (useMemo con deps como
 * columnOptions) para que initialColumns solo cambie cuando cambien realmente
 * las definiciones.
 */
export const useTableColumns = <T>(initialColumns: ColumnConfig<T>[]) => {
  const [columns, setColumns] = useState<ColumnConfig<T>[]>(initialColumns);

  useEffect(() => {
    setColumns((prev) => {
      const newById = new Map(initialColumns.map((c) => [c.id, c]));
      const prevIds = new Set(prev.map((c) => c.id));
      const newIds = new Set(initialColumns.map((c) => c.id));
      const sameSet = prevIds.size === newIds.size && [...prevIds].every((id) => newIds.has(id));

      const merge = (newCol: ColumnConfig<T>, current: ColumnConfig<T> | undefined) => {
        if (!current) {
          const width = typeof newCol.width === 'number' ? newCol.width : undefined;
          return { ...newCol, originalWidth: width ?? newCol.originalWidth };
        }
        const width = current.width ?? newCol.width;
        const originalWidth =
          current.originalWidth ??
          (typeof newCol.width === 'number' ? newCol.width : newCol.originalWidth);
        return {
          ...newCol,
          width,
          originalWidth,
          visible: current.visible !== undefined ? current.visible : newCol.visible,
          locked: current.locked ?? newCol.locked,
        };
      };

      let result: ColumnConfig<T>[];
      if (sameSet) {
        result = prev.map((current) => {
          const newCol = newById.get(current.id);
          return newCol ? merge(newCol, current) : current;
        });
      } else {
        result = initialColumns.map((newCol) =>
          merge(
            newCol,
            prev.find((c) => c.id === newCol.id),
          ),
        );
      }

      // Avoid triggering a re-render when the merge produces structurally identical columns.
      // .map() always returns a new array reference, so we compare key properties.
      if (
        result.length === prev.length &&
        result.every((col, i) => {
          const p = prev[i];
          return (
            col.id === p.id &&
            col.width === p.width &&
            col.originalWidth === p.originalWidth &&
            col.visible === p.visible &&
            col.locked === p.locked &&
            col.columnOptions === p.columnOptions &&
            col.columnOptionsLoading === p.columnOptionsLoading &&
            col.editable === p.editable &&
            col.header === p.header &&
            areColumnCellDefinitionsEqual(col, p)
          );
        })
      ) {
        return prev;
      }

      return result;
    });
  }, [initialColumns]);

  const handleToggleVisibility = useCallback((columnId: string) => {
    setColumns((prev) => toggleColumnVisibility(prev, columnId));
  }, []);

  const handleSetVisibility = useCallback((columnId: string, visible: boolean) => {
    setColumns((prev) => setColumnVisibility(prev, columnId, visible));
  }, []);

  const handleToggleLock = useCallback((columnId: string) => {
    setColumns((prev) => toggleColumnLock(prev, columnId));
  }, []);

  const handleResize = useCallback((columnId: string, width: number) => {
    setColumns((prev) => resizeColumn(prev, columnId, width));
  }, []);

  const handleReorder = useCallback((fromIndex: number, toIndex: number) => {
    setColumns((prev) => reorderColumns(prev, fromIndex, toIndex));
  }, []);

  const setColumnsOrder = useCallback((orderedColumnIds: string[]) => {
    setColumns((prev) => {
      // Crear un mapa de id a columna para búsqueda rápida
      const columnMap = new Map(prev.map((col) => [col.id, col]));

      // Reordenar según el array de IDs
      const reordered = orderedColumnIds
        .map((id) => columnMap.get(id))
        .filter((col): col is NonNullable<typeof col> => col !== undefined);

      // Agregar cualquier columna que no esté en orderedColumnIds al final
      const includedIds = new Set(orderedColumnIds);
      const remaining = prev.filter((col) => !includedIds.has(col.id));

      return [...reordered, ...remaining];
    });
  }, []);

  const resetColumns = useCallback(() => {
    setColumns(initialColumns);
  }, [initialColumns]);

  return {
    columns,
    handleToggleVisibility,
    handleSetVisibility,
    handleToggleLock,
    handleResize,
    handleReorder,
    setColumnsOrder,
    resetColumns,
  };
};
